import {
  isJsonObject,
  optionalNullableString,
  requiredString,
  type JsonObject,
  type JsonValue,
} from "@/components/guardians/jsonValue";
import type {
  GateBlockedDisplay,
  GateBlockedReason,
  TriggerCreatedPayload,
  TriggerStatusChangedEvent,
  V2AgentRef,
  V2EntityRef,
  V2InteractionCompletedPayload,
  V2InteractionPhase,
  V2InteractionProgressPayload,
  V2InteractionStartedPayload,
  V2MeetingCreatedPayload,
  V2MeetingUpdatedPayload,
  V2MissionCreatedPayload,
  V2MissionCompletedPayload,
  V2MissionStageChangedPayload,
  V2TaskCreatedPayload,
  V2TaskNeedsAttentionPayload,
  V2TaskStatusChangedPayload,
  V2WsTaskRow,
} from "./MissionControlWebSocketContext";
import type {
  InteractionStep,
  MissionSummary,
  TaskActionItem,
  TaskAgentRef,
  TaskEntityRef,
  TaskMissionRef,
  ThreadMessage,
} from "@/services/api/agentic-v2";

/**
 * Runtime parsers for agentic v2 WebSocket envelopes.
 * Validates JSON wire shapes before handing typed payloads to Mission Control handlers.
 */

function parseGateBlockedReason(value: JsonValue | undefined): GateBlockedReason | null {
  if (!isJsonObject(value)) return null;
  const path = requiredString(value, "path");
  const op = requiredString(value, "op");
  if (!path || !op) return null;
  const reason: GateBlockedReason = { path, op };
  const expected = value.expected;
  if (expected !== undefined) reason.expected = expected;
  const actual = value.actual;
  if (actual !== undefined) reason.actual = actual;
  return reason;
}

function parseGateBlockedDisplay(value: JsonValue | undefined): GateBlockedDisplay | null {
  if (!isJsonObject(value)) return null;
  const title = requiredString(value, "title");
  const body = requiredString(value, "body");
  if (!title || !body) return null;
  return { title, body };
}

function parseAgentRef(value: JsonValue | undefined): V2AgentRef | null {
  if (!isJsonObject(value)) return null;
  const id = requiredString(value, "id");
  if (!id) return null;
  const parsedName = requiredString(value, "name");
  const name = parsedName?.trim() ? parsedName : "Agent";
  const agent: V2AgentRef = { id, name };
  const role = optionalNullableString(value.role);
  if (role !== undefined) agent.role = role;
  return agent;
}

function parseEntityRef(value: JsonValue | undefined): V2EntityRef | null {
  if (!isJsonObject(value)) return null;
  const entity_type = requiredString(value, "entity_type");
  const entity_id = requiredString(value, "entity_id");
  if (!entity_type || !entity_id) return null;
  const entity: V2EntityRef = { entity_type, entity_id };
  const display_name = optionalNullableString(value.display_name);
  if (display_name !== undefined) entity.display_name = display_name;
  const lead_detail = optionalNullableString(value.lead_detail);
  if (lead_detail !== undefined) entity.lead_detail = lead_detail;
  const lead_status = optionalNullableString(value.lead_status);
  if (lead_status !== undefined) entity.lead_status = lead_status;
  return entity;
}

function parseEntityRefs(value: JsonValue | undefined): V2EntityRef[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => parseEntityRef(entry))
    .filter((entry): entry is V2EntityRef => entry !== null);
}

function parseStringArray(value: JsonValue | undefined): string[] | null {
  if (!Array.isArray(value)) return null;
  if (!value.every((entry) => typeof entry === "string")) return null;
  return value;
}

function parseTaskAgentRef(value: JsonValue | undefined): TaskAgentRef | null {
  if (value === null || value === undefined) return null;
  if (!isJsonObject(value)) return null;
  const id = requiredString(value, "id");
  if (!id) return null;
  const parsedName = requiredString(value, "name");
  const name = parsedName?.trim() ? parsedName : "Agent";
  const agent: TaskAgentRef = { id, name };
  const role = optionalNullableString(value.role);
  if (role !== undefined) agent.role = role;
  const avatar_colour = optionalNullableString(value.avatar_colour);
  if (avatar_colour !== undefined) agent.avatar_colour = avatar_colour;
  return agent;
}

function parseTaskMissionRef(value: JsonValue | undefined): TaskMissionRef | null {
  if (value === null || value === undefined) return null;
  if (!isJsonObject(value)) return null;
  const mission: TaskMissionRef = {
    id: optionalNullableString(value.id) ?? null,
    name: optionalNullableString(value.name) ?? null,
    current_stage: optionalNullableString(value.current_stage) ?? null,
  };
  const status = optionalNullableString(value.status);
  if (status !== undefined) mission.status = status;
  const current_stage_display = optionalNullableString(value.current_stage_display);
  if (current_stage_display !== undefined) {
    mission.current_stage_display = current_stage_display;
  }
  return mission;
}

function parseTaskEntityRef(value: JsonValue | undefined): TaskEntityRef | null {
  if (value === null || value === undefined) return null;
  if (!isJsonObject(value)) return null;
  const entity: TaskEntityRef = {};
  const entity_type = optionalNullableString(value.entity_type);
  if (entity_type !== undefined) entity.entity_type = entity_type;
  const entity_id = optionalNullableString(value.entity_id);
  if (entity_id !== undefined) entity.entity_id = entity_id;
  const display_name = optionalNullableString(value.display_name);
  if (display_name !== undefined) entity.display_name = display_name;
  const lead_detail = optionalNullableString(value.lead_detail);
  if (lead_detail !== undefined) entity.lead_detail = lead_detail;
  const lead_status = optionalNullableString(value.lead_status);
  if (lead_status !== undefined) entity.lead_status = lead_status;
  return entity;
}

function parseTaskActions(value: JsonValue | undefined): (TaskActionItem["actions"][number])[] {
  if (!Array.isArray(value)) return [];
  const actions: TaskActionItem["actions"] = [];
  for (const entry of value) {
    if (typeof entry === "string") {
      actions.push(entry);
      continue;
    }
    if (!isJsonObject(entry)) continue;
    const key = requiredString(entry, "key");
    if (!key) continue;
    actions.push({ key });
  }
  return actions;
}

/** Build a ``TaskActionItem`` from an optional hydrated ``task`` field on WS payloads. */
export function parseWsTaskRow(value: JsonValue | undefined): V2WsTaskRow | null {
  if (!isJsonObject(value)) return null;
  const _id = requiredString(value, "_id");
  const name = requiredString(value, "name");
  const status = requiredString(value, "status");
  const task_type = requiredString(value, "task_type");
  const priority = requiredString(value, "priority");
  const updated_at = requiredString(value, "updated_at");
  if (!_id || !name || !status || !task_type || !priority || !updated_at) return null;
  const tags = parseStringArray(value.tags);
  if (tags === null) return null;
  const requires_review = value.requires_review;
  if (typeof requires_review !== "boolean") return null;
  const definition_of_done = optionalNullableString(value.definition_of_done) ?? null;
  const agent = parseTaskAgentRef(value.agent);
  const mission = parseTaskMissionRef(value.mission);
  const entity = parseTaskEntityRef(value.entity);
  const task: TaskActionItem = {
    _id,
    name,
    status,
    task_type,
    priority,
    tags,
    actions: parseTaskActions(value.actions),
    requires_review,
    definition_of_done,
    agent,
    mission,
    entity,
    queued_at: optionalNullableString(value.queued_at) ?? null,
    started_at: optionalNullableString(value.started_at) ?? null,
    completed_at: optionalNullableString(value.completed_at) ?? null,
    updated_at,
  };
  const disposition_reasoning = optionalNullableString(value.disposition_reasoning);
  if (disposition_reasoning !== undefined) {
    task.disposition_reasoning = disposition_reasoning;
  }
  const confidence = optionalNullableString(value.confidence);
  if (
    confidence === "low" ||
    confidence === "medium" ||
    confidence === "high" ||
    confidence === null
  ) {
    task.confidence = confidence;
  }
  const blockers = parseStringArray(value.blockers);
  if (blockers !== null) task.blockers = blockers;
  const gate_blocked_reason =
    value.gate_blocked_reason === null
      ? null
      : parseGateBlockedReason(value.gate_blocked_reason);
  if (gate_blocked_reason !== null || value.gate_blocked_reason === null) {
    task.gate_blocked_reason = gate_blocked_reason;
  }
  const gate_blocked_display =
    value.gate_blocked_display === null
      ? null
      : parseGateBlockedDisplay(value.gate_blocked_display);
  if (gate_blocked_display !== null || value.gate_blocked_display === null) {
    task.gate_blocked_display = gate_blocked_display;
  }
  const latest_summary = optionalNullableString(value.latest_summary);
  if (latest_summary !== undefined) task.latest_summary = latest_summary;
  const assigned_human_id = optionalNullableString(value.assigned_human_id);
  if (assigned_human_id !== undefined) task.assigned_human_id = assigned_human_id;
  const assigned_human_email = optionalNullableString(value.assigned_human_email);
  if (assigned_human_email !== undefined) task.assigned_human_email = assigned_human_email;
  const assigned_human_name = optionalNullableString(value.assigned_human_name);
  if (assigned_human_name !== undefined) task.assigned_human_name = assigned_human_name;
  const auto_closed_reason = optionalNullableString(value.auto_closed_reason);
  if (auto_closed_reason !== undefined) task.auto_closed_reason = auto_closed_reason;
  const auto_closed_display = optionalNullableString(value.auto_closed_display);
  if (auto_closed_display !== undefined) task.auto_closed_display = auto_closed_display;
  const spawned_from_node = optionalNullableString(value.spawned_from_node);
  if (spawned_from_node !== undefined) task.spawned_from_node = spawned_from_node;
  const stage = optionalNullableString(value.stage);
  if (stage !== undefined) task.stage = stage;
  const catalogue_id = optionalNullableString(value.catalogue_id);
  if (catalogue_id !== undefined) task.catalogue_id = catalogue_id;
  const kit_slug = optionalNullableString(value.kit_slug);
  if (kit_slug !== undefined) task.kit_slug = kit_slug;
  const user_ask = optionalNullableString(value.user_ask);
  if (user_ask !== undefined) task.user_ask = user_ask;
  return task;
}

/** Build a ``ThreadMessage`` from an optional ``message`` delta on interaction progress. */
function parseThreadMessage(value: JsonValue | undefined): ThreadMessage | null {
  if (!isJsonObject(value)) return null;
  const _id = requiredString(value, "_id");
  const sender_type = requiredString(value, "sender_type");
  const content_type = requiredString(value, "content_type");
  const created_at = requiredString(value, "created_at");
  if (!_id || !sender_type || !content_type || !created_at) return null;
  const contentValue = value.content;
  const content = isJsonObject(contentValue) ? contentValue : {};
  const message: ThreadMessage = {
    _id,
    sender_type,
    content_type,
    content,
    created_at,
  };
  const task_id = optionalNullableString(value.task_id);
  if (task_id !== undefined) message.task_id = task_id;
  const mission_id = optionalNullableString(value.mission_id);
  if (mission_id !== undefined) message.mission_id = mission_id;
  const interaction_id = optionalNullableString(value.interaction_id);
  if (interaction_id !== undefined) message.interaction_id = interaction_id;
  const sender_id = optionalNullableString(value.sender_id);
  if (sender_id !== undefined) message.sender_id = sender_id;
  return message;
}

/** Build an ``InteractionStep`` from an optional ``step`` delta on interaction progress. */
function parseInteractionStep(value: JsonValue | undefined): InteractionStep | null {
  if (!isJsonObject(value)) return null;
  const type = requiredString(value, "type");
  if (!type) return null;
  const step: InteractionStep = { type };
  const content = optionalNullableString(value.content);
  if (content !== undefined) step.content = content;
  const tool = optionalNullableString(value.tool);
  if (tool !== undefined) step.tool = tool;
  const tool_display_name = optionalNullableString(value.tool_display_name);
  if (tool_display_name !== undefined) step.tool_display_name = tool_display_name;
  if (value.input === null) {
    step.input = null;
  } else if (isJsonObject(value.input)) {
    step.input = value.input;
  }
  if (value.output === null) {
    step.output = null;
  } else if (isJsonObject(value.output)) {
    step.output = value.output;
  }
  if (typeof value.duration_ms === "number") step.duration_ms = value.duration_ms;
  const timestamp = optionalNullableString(value.timestamp);
  if (timestamp !== undefined) step.timestamp = timestamp;
  const thinking = optionalNullableString(value.thinking);
  if (thinking !== undefined) step.thinking = thinking;
  const summary = optionalNullableString(value.summary);
  if (summary !== undefined) step.summary = summary;
  const disposition = optionalNullableString(value.disposition);
  if (disposition !== undefined) step.disposition = disposition;
  const disposition_reasoning = optionalNullableString(value.disposition_reasoning);
  if (disposition_reasoning !== undefined) step.disposition_reasoning = disposition_reasoning;
  const confidence = optionalNullableString(value.confidence);
  if (confidence !== undefined) step.confidence = confidence;
  const blockers = parseStringArray(value.blockers);
  if (blockers !== null) step.blockers = blockers;
  const raw_response = optionalNullableString(value.raw_response);
  if (raw_response !== undefined) step.raw_response = raw_response;
  const raw_prompt = optionalNullableString(value.raw_prompt);
  if (raw_prompt !== undefined) step.raw_prompt = raw_prompt;
  const model = optionalNullableString(value.model);
  if (model !== undefined) step.model = model;
  return step;
}

/** Parse optional hydrated Mission Control mission row on WS payloads. */
export function parseWsMissionRow(value: JsonValue | undefined): MissionSummary | null {
  if (!isJsonObject(value)) return null;
  const _id = requiredString(value, "_id");
  const client_id = requiredString(value, "client_id");
  const template_id = requiredString(value, "template_id");
  const status = requiredString(value, "status");
  const current_stage = requiredString(value, "current_stage");
  const updated_at = requiredString(value, "updated_at");
  const created_at = requiredString(value, "created_at");
  if (
    !_id ||
    !client_id ||
    !template_id ||
    !status ||
    !current_stage ||
    !updated_at ||
    !created_at
  ) {
    return null;
  }
  const attentionValue = value.attention;
  if (!isJsonObject(attentionValue)) return null;
  const needs_attention = attentionValue.needs_attention === true;
  const blocked_task_count =
    typeof attentionValue.blocked_task_count === "number"
      ? attentionValue.blocked_task_count
      : 0;
  const review_task_count =
    typeof attentionValue.review_task_count === "number"
      ? attentionValue.review_task_count
      : 0;
  const progress_pct =
    typeof value.progress_pct === "number" ? value.progress_pct : 0;
  const assigned = parseStringArray(value.assigned_agents);
  return {
    _id,
    client_id,
    template_id,
    template_name: optionalNullableString(value.template_name) ?? null,
    status,
    current_stage,
    current_stage_display: optionalNullableString(value.current_stage_display) ?? null,
    current_lane: optionalNullableString(value.current_lane) ?? null,
    current_lane_display: optionalNullableString(value.current_lane_display) ?? null,
    entities: parseEntityRefs(value.entities),
    assigned_agents: assigned ?? [],
    human_owner: optionalNullableString(value.human_owner) ?? null,
    attention: {
      needs_attention,
      reason: optionalNullableString(attentionValue.reason) ?? null,
      blocked_task_count,
      review_task_count,
    },
    progress_pct,
    updated_at,
    created_at,
    kit_slug: optionalNullableString(value.kit_slug) ?? null,
    kit_version: optionalNullableString(value.kit_version) ?? null,
    name: optionalNullableString(value.name) ?? null,
  };
}

/** Parse ``v2.mission.created`` payload from a JSON value. */
export function parseV2MissionCreatedPayload(
  value: JsonValue | undefined
): V2MissionCreatedPayload | null {
  if (!isJsonObject(value)) return null;
  const mission_id = requiredString(value, "mission_id");
  const template_id = requiredString(value, "template_id");
  const current_stage = requiredString(value, "current_stage");
  const status = requiredString(value, "status");
  if (!mission_id || !template_id || !current_stage || !status) return null;
  const template_name = optionalNullableString(value.template_name);
  const name = optionalNullableString(value.name);
  const human_owner = optionalNullableString(value.human_owner);
  return {
    mission_id,
    template_id,
    template_name: template_name ?? null,
    name: name ?? null,
    current_stage,
    human_owner: human_owner ?? null,
    status,
    entities: parseEntityRefs(value.entities),
    mission: parseWsMissionRow(value.mission),
  };
}

/** Parse ``v2.mission.stage_changed`` payload from a JSON value. */
export function parseV2MissionStageChangedPayload(
  value: JsonValue | undefined
): V2MissionStageChangedPayload | null {
  if (!isJsonObject(value)) return null;
  const mission_id = requiredString(value, "mission_id");
  const new_stage = requiredString(value, "new_stage");
  if (!mission_id || !new_stage) return null;
  const mission_name = optionalNullableString(value.mission_name);
  const old_stage = optionalNullableString(value.old_stage);
  return {
    mission_id,
    mission_name: mission_name ?? null,
    old_stage: old_stage ?? null,
    new_stage,
    mission: parseWsMissionRow(value.mission),
  };
}

/** Parse ``v2.mission.completed`` payload from a JSON value. */
export function parseV2MissionCompletedPayload(
  value: JsonValue | undefined
): V2MissionCompletedPayload | null {
  if (!isJsonObject(value)) return null;
  const mission_id = requiredString(value, "mission_id");
  const status = requiredString(value, "status");
  if (!mission_id || !status) return null;
  const mission_name = optionalNullableString(value.mission_name);
  return {
    mission_id,
    mission_name: mission_name ?? null,
    status,
    mission: parseWsMissionRow(value.mission),
  };
}

/** Parse ``v2.task.created`` payload from a JSON value. */
export function parseV2TaskCreatedPayload(
  value: JsonValue | undefined
): V2TaskCreatedPayload | null {
  if (!isJsonObject(value)) return null;
  const task_id = requiredString(value, "task_id");
  const name = requiredString(value, "name");
  const status = requiredString(value, "status");
  const task_type = requiredString(value, "task_type");
  if (!task_id || !name || !status || !task_type) return null;
  const mission_id = optionalNullableString(value.mission_id);
  const catalogue_id = optionalNullableString(value.catalogue_id);
  const assigned_agent_id = optionalNullableString(value.assigned_agent_id);
  const spawned_from = optionalNullableString(value.spawned_from);
  const spawned_from_node = optionalNullableString(value.spawned_from_node);
  const stage = optionalNullableString(value.stage);
  const agentValue = value.agent;
  const agent =
    agentValue === null ? null : parseAgentRef(agentValue);
  const entityValue = value.entity;
  const entity =
    entityValue === null ? null : parseEntityRef(entityValue);
  const gate_blocked_reason =
    value.gate_blocked_reason === null
      ? null
      : parseGateBlockedReason(value.gate_blocked_reason);
  return {
    task_id,
    mission_id: mission_id ?? null,
    name,
    status,
    task_type,
    catalogue_id: catalogue_id ?? null,
    assigned_agent_id: assigned_agent_id ?? null,
    agent,
    entity,
    spawned_from: spawned_from ?? null,
    spawned_from_node: spawned_from_node ?? null,
    stage: stage ?? undefined,
    gate_blocked_reason,
    task: parseWsTaskRow(value.task),
  };
}

/** Parse ``v2.task.status_changed`` payload from a JSON value. */
export function parseV2TaskStatusChangedPayload(
  value: JsonValue | undefined
): V2TaskStatusChangedPayload | null {
  if (!isJsonObject(value)) return null;
  const task_id = requiredString(value, "task_id");
  const name = requiredString(value, "name");
  const new_status = requiredString(value, "new_status");
  if (!task_id || !name || !new_status) return null;
  const mission_id = optionalNullableString(value.mission_id);
  const old_status = optionalNullableString(value.old_status);
  const action = optionalNullableString(value.action);
  const agentValue = value.agent;
  const agent =
    agentValue === null ? null : parseAgentRef(agentValue);
  const entityValue = value.entity;
  const entity =
    entityValue === null ? null : parseEntityRef(entityValue);
  const stage = optionalNullableString(value.stage);
  const spawned_from_node = optionalNullableString(value.spawned_from_node);
  return {
    task_id,
    mission_id: mission_id ?? null,
    name,
    old_status: old_status ?? null,
    new_status,
    action: action ?? null,
    agent,
    entity,
    stage: stage ?? undefined,
    spawned_from_node: spawned_from_node ?? undefined,
    task: parseWsTaskRow(value.task),
    mission: parseWsMissionRow(value.mission),
  };
}

/** Parse ``v2.task.needs_attention`` payload from a JSON value. */
export function parseV2TaskNeedsAttentionPayload(
  value: JsonValue | undefined
): V2TaskNeedsAttentionPayload | null {
  if (!isJsonObject(value)) return null;
  const task_id = requiredString(value, "task_id");
  const name = requiredString(value, "name");
  const status = requiredString(value, "status");
  if (!task_id || !name || !status) return null;
  const mission_id = optionalNullableString(value.mission_id);
  const reason = optionalNullableString(value.reason);
  const blockers = parseStringArray(value.blockers);
  const gate_blocked_reason =
    value.gate_blocked_reason === null
      ? null
      : parseGateBlockedReason(value.gate_blocked_reason);
  const gate_blocked_display =
    value.gate_blocked_display === null
      ? null
      : parseGateBlockedDisplay(value.gate_blocked_display);
  const agentValue = value.agent;
  const agent =
    agentValue === null ? null : parseAgentRef(agentValue);
  const entityValue = value.entity;
  const entity =
    entityValue === null ? null : parseEntityRef(entityValue);
  return {
    task_id,
    mission_id: mission_id ?? null,
    name,
    status,
    reason: reason ?? null,
    blockers,
    gate_blocked_reason,
    gate_blocked_display,
    agent,
    entity,
    mission: parseWsMissionRow(value.mission),
  };
}

/** Parse ``v2.interaction.started`` payload from a JSON value. */
export function parseV2InteractionStartedPayload(
  value: JsonValue | undefined
): V2InteractionStartedPayload | null {
  if (!isJsonObject(value)) return null;
  const interaction_id = requiredString(value, "interaction_id");
  if (!interaction_id) return null;
  const task_id = optionalNullableString(value.task_id);
  const mission_id = optionalNullableString(value.mission_id);
  const agent_id = optionalNullableString(value.agent_id);
  return {
    interaction_id,
    task_id: task_id ?? null,
    mission_id: mission_id ?? null,
    agent_id: agent_id ?? null,
  };
}

/** Parse ``v2.interaction.completed`` payload from a JSON value. */
export function parseV2InteractionCompletedPayload(
  value: JsonValue | undefined
): V2InteractionCompletedPayload | null {
  if (!isJsonObject(value)) return null;
  const interaction_id = requiredString(value, "interaction_id");
  const status = requiredString(value, "status");
  if (!interaction_id || !status) return null;
  const task_id = optionalNullableString(value.task_id);
  const mission_id = optionalNullableString(value.mission_id);
  const agent_id = optionalNullableString(value.agent_id);
  return {
    interaction_id,
    task_id: task_id ?? null,
    mission_id: mission_id ?? null,
    agent_id: agent_id ?? null,
    status,
  };
}

const INTERACTION_PHASES: readonly V2InteractionPhase[] = [
  "thinking",
  "tool_call",
  "tool_result",
  "message",
];

function isInteractionPhase(value: JsonValue | undefined): value is V2InteractionPhase {
  return (
    typeof value === "string" &&
    (INTERACTION_PHASES as readonly string[]).includes(value)
  );
}

/** Parse ``v2.interaction.progress`` payload from a JSON value. */
export function parseV2InteractionProgressPayload(
  value: JsonValue | undefined
): V2InteractionProgressPayload | null {
  if (!isJsonObject(value)) return null;
  const interaction_id = requiredString(value, "interaction_id");
  if (!interaction_id) return null;
  const phase = value.phase;
  if (!isInteractionPhase(phase)) return null;
  const task_id = optionalNullableString(value.task_id);
  const mission_id = optionalNullableString(value.mission_id);
  const agent_id = optionalNullableString(value.agent_id);
  const tool = optionalNullableString(value.tool);
  const label = optionalNullableString(value.label);
  const step_index =
    typeof value.step_index === "number" ? value.step_index : null;
  const ok = typeof value.ok === "boolean" ? value.ok : null;
  return {
    interaction_id,
    task_id: task_id ?? null,
    mission_id: mission_id ?? null,
    agent_id: agent_id ?? null,
    phase,
    tool: tool ?? null,
    step_index,
    ok,
    label: label ?? null,
    thread_dirty: value.thread_dirty === true,
    step: parseInteractionStep(value.step),
    message: parseThreadMessage(value.message),
  };
}

/** Parse ``trigger_created`` trigger object from a JSON value. */
export function parseTriggerCreatedPayload(
  value: JsonValue | undefined
): TriggerCreatedPayload | null {
  if (!isJsonObject(value)) return null;
  const id = requiredString(value, "id");
  const template_id = requiredString(value, "template_id");
  const pod_instance_id = requiredString(value, "pod_instance_id");
  const trigger_type = requiredString(value, "trigger_type");
  const status = requiredString(value, "status");
  const created_at = requiredString(value, "created_at");
  if (!id || !template_id || !pod_instance_id || !trigger_type || !status || !created_at) {
    return null;
  }
  const template_name = optionalNullableString(value.template_name);
  const pod_instance_name = optionalNullableString(value.pod_instance_name);
  const trigger_source = optionalNullableString(value.trigger_source);
  const interaction_id = optionalNullableString(value.interaction_id);
  const documents_created = value.documents_created;
  const pod_instance_status = optionalNullableString(value.pod_instance_status);
  return {
    id,
    template_id,
    template_name: template_name ?? null,
    pod_instance_id,
    pod_instance_name: pod_instance_name ?? null,
    trigger_type,
    trigger_source: trigger_source ?? null,
    status,
    interaction_id: interaction_id ?? null,
    created_at,
    documents_created: typeof documents_created === "number" ? documents_created : 0,
    pod_instance_status: pod_instance_status ?? undefined,
  };
}

/** Parse a ``trigger_status_changed`` envelope from a JSON object. */
export function parseTriggerStatusChangedEvent(
  envelope: JsonObject
): TriggerStatusChangedEvent | null {
  const type = envelope.type;
  if (type !== "trigger_status_changed") return null;
  const company_id = requiredString(envelope, "company_id");
  const trigger_id = requiredString(envelope, "trigger_id");
  const pod_instance_id = requiredString(envelope, "pod_instance_id");
  const interaction_id = requiredString(envelope, "interaction_id");
  const new_state = requiredString(envelope, "new_state");
  const updated_at = requiredString(envelope, "updated_at");
  if (
    !company_id ||
    !trigger_id ||
    !pod_instance_id ||
    !interaction_id ||
    !new_state ||
    !updated_at
  ) {
    return null;
  }
  const old_state = optionalNullableString(envelope.old_state);
  const pod_instance_status = optionalNullableString(envelope.pod_instance_status);
  return {
    type: "trigger_status_changed",
    company_id,
    trigger_id,
    pod_instance_id,
    interaction_id,
    old_state: old_state ?? null,
    new_state,
    updated_at,
    pod_instance_status: pod_instance_status ?? undefined,
  };
}

/** Read the ``payload`` field from a parsed WS envelope object. */
export function wsEnvelopePayload(envelope: JsonObject): JsonValue | undefined {
  return envelope.payload;
}

/** Read the ``trigger`` field from a parsed WS envelope object. */
export function wsEnvelopeTrigger(envelope: JsonObject): JsonValue | undefined {
  return envelope.trigger;
}

/** Read ``company_id`` from a control envelope when it is a string. */
export function wsEnvelopeCompanyId(envelope: JsonObject): string | null {
  return requiredString(envelope, "company_id");
}

/** Parse ``v2.meeting.created`` payload. */
export function parseV2MeetingCreatedPayload(
  value: JsonValue | undefined,
): V2MeetingCreatedPayload | null {
  if (!isJsonObject(value)) return null;
  const meetingId = requiredString(value, "meetingId");
  const companyId = requiredString(value, "companyId");
  const title = requiredString(value, "title");
  const status = requiredString(value, "status");
  if (!meetingId || !companyId || !title || !status) return null;
  return { meetingId, companyId, title, status };
}

/** Parse ``v2.meeting.updated`` payload. */
export function parseV2MeetingUpdatedPayload(
  value: JsonValue | undefined,
): V2MeetingUpdatedPayload | null {
  if (!isJsonObject(value)) return null;
  const meetingId = requiredString(value, "meetingId");
  const companyId = requiredString(value, "companyId");
  if (!meetingId || !companyId) return null;
  const payload: V2MeetingUpdatedPayload = { meetingId, companyId };
  const title = requiredString(value, "title");
  if (title) payload.title = title;
  const status = requiredString(value, "status");
  if (status) payload.status = status;
  if ("currentTaskId" in value) {
    const taskId = requiredString(value, "currentTaskId");
    payload.currentTaskId = taskId ?? null;
  }
  if (typeof value.deleted === "boolean") {
    payload.deleted = value.deleted;
  }
  return payload;
}
